import { Enums } from "./enums";
import { getTicketStatusEntity } from "../entity-service/global-status-entity-service";

/** Same surface as summary grid `ticketStatusObj` for icon/color/text rendering. */
export type TicketStatusListUi = {
  name: string;
  statusIcon: string;
  statusColor: string;
  percentageOfStatusIcon: number | null;
};

export class TicketHelper {
  static getTicketStatusIcon(baseStatusId: number): string {
    if (baseStatusId == Enums.TicketStatus.New) {
      return "circleStroke";
    } else if (baseStatusId == Enums.TicketStatus.Open) {
      return "opencustomicon";
    } else if (baseStatusId == Enums.TicketStatus.OnHold) {
      return "minusSignCircle";
    } else {
      return "checkmarkCircle02";
    }
  }

  static calculatePercentageOfTicketStatus(
    baseStatusId: number,
    totalCountOfStatusesInBaseStatus: number,
    currentIndexInsideBaseStatus: number,
  ): number | null {
    if (baseStatusId == Enums.TicketStatus.New) {
      return 0;
    } else if (baseStatusId == Enums.TicketStatus.Open) {
      let totalStatuses = totalCountOfStatusesInBaseStatus + 1; //Solved
      return ((currentIndexInsideBaseStatus + 1) / totalStatuses) * 100;
    } else if (
      baseStatusId == Enums.TicketStatus.Solved ||
      baseStatusId == Enums.TicketStatus.Closed
    ) {
      return 100;
    }

    return null;
  }

  /**
   * Status chip data for portal message list (same source as ticket-summary rows).
   * Falls back to {@link TicketHelper.getTicketStatusIcon} / neutral color when the custom
   * record omits fields (e.g. merged shortcuts).
   */
  static resolveStatusForMessageList(
    ticketStatusId: number | null | undefined,
  ): TicketStatusListUi | null {
    if (ticketStatusId == null || ticketStatusId <= 0) {
      return null;
    }

    const entity = getTicketStatusEntity(ticketStatusId);
    if (!entity) {
      return null;
    }

    const baseId = Number(entity.id);
    const iconFromEntity = entity.statusIcon
      ? String(entity.statusIcon).trim()
      : "";
    const statusIcon =
      iconFromEntity.length > 0
        ? iconFromEntity
        : TicketHelper.getTicketStatusIcon(baseId);

    const colorFromEntity = entity.statusColor
      ? String(entity.statusColor).trim()
      : "";
    const statusColor =
      colorFromEntity.length > 0
        ? colorFromEntity
        : "var(--color-text-tertiary)";

    const rawPct = entity.percentageOfStatusIcon;
    const parsedPct = Number(rawPct);
    const percentageOfStatusIcon =
      rawPct != null &&
      rawPct !== "" &&
      Number.isFinite(parsedPct)
        ? parsedPct
        : null;

    return {
      name: String(entity.name ?? ""),
      statusIcon,
      statusColor,
      percentageOfStatusIcon,
    };
  }

  /**
   * Background/border tint for summary/header chips (`#RRGGBB` + alpha suffix pattern).
   * Returns null for CSS-variable colors etc. so the caller falls back to default badge chrome.
   */
  static ticketStatusBadgeSurfaceStyle(
    statusColor: string,
  ): Record<string, string> | null {
    const c = String(statusColor || "").trim();
    if (/^#[0-9A-Fa-f]{6}$/.test(c)) {
      return {
        backgroundColor: `${c}33`,
        borderColor: `${c}4D`,
      };
    }
    return null;
  }
}
